<template>
  <v-container
    class="economic-calendar-page responsive-height main-container"
    :class="{ 'calendar-dark': $vuetify.theme.dark }"
  >
    <v-card elevation="0" class="calendar-shell">
      <div class="calendar-header">
        <div>
          <p class="section-label mb-1">Market schedule</p>
          <h2 class="mb-1">Economic Calendar</h2>
          <p class="calendar-subtitle mb-0">
            Track macro events, forecasts, and actual releases by country and impact.
          </p>
        </div>

        <v-btn
          :color="$vuetify.theme.dark ? '#1A2A3E ' : '#F8F6FC'"
            elevation="0"
          :loading="loading"
          @click="fetchEconomicData"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>

      <v-row dense class="summary-row">
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card">
            <span>Total Events</span>
            <strong>{{ filteredEvents.length }}</strong>
          </div>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card summary-card--high">
            <span>High Impact</span>
            <strong>{{ highImpactCount }}</strong>
          </div>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card">
            <span>Countries</span>
            <strong>{{ selectedCountries.length }}</strong>
          </div>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card">
            <span>Last Update</span>
            <strong>{{ lastLoadedText }}</strong>
          </div>
        </v-col>
      </v-row>

      <v-card elevation="0" class="filter-panel">
        <div class="filter-header">
          <div>
            <p class="filter-eyebrow mb-1">Calendar controls</p>
            <h3 class="mb-0">Filter market events</h3>
          </div>

          <div class="filter-actions">
            <v-btn text small class="reset-filter-btn" @click="resetFilters">
              <v-icon left small>mdi-restore</v-icon>
              Reset
            </v-btn>
            <v-btn
              tile
              small
              :color="$vuetify.theme.dark ? '#1A2A3E ' : '#F8F6FC'"
              elevation="0"
              :loading="loading"
              @click="fetchEconomicData"
            >
              <v-icon left small>mdi-filter-check-outline</v-icon>
              Apply Filters
            </v-btn>
          </div>
        </div>

        <div class="quick-range-row">
          <span>Quick range</span>
          <v-chip
            v-for="preset in datePresets"
            :key="preset.value"
            small
            label
            class="quick-range-chip"
            :class="{ 'quick-range-chip--active': activeDatePreset === preset.value }"
            @click="applyDatePreset(preset.value, true)"
          >
            {{ preset.text }}
          </v-chip>
        </div>

        <v-row dense class="filter-grid">
          <v-col cols="12" sm="6" lg="3">
            <label class="control-label">Start</label>
            <v-text-field
              v-model="startDate"
              type="date"
              prepend-inner-icon="mdi-calendar-start"
              hide-details
              outlined
              dense
              @input="activeDatePreset = 'custom'"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <label class="control-label">End</label>
            <v-text-field
              v-model="endDate"
              type="date"
              prepend-inner-icon="mdi-calendar-end"
              hide-details
              outlined
              dense
              @input="activeDatePreset = 'custom'"
            ></v-text-field>
          </v-col>

          <v-col cols="12" lg="3">
            <label class="control-label">Countries</label>
            <v-select
              v-model="selectedCountries"
              :items="countryOptions"
              item-text="text"
              item-value="value"
              multiple
              chips
              small-chips
              deletable-chips
              hide-details
              outlined
              dense
              placeholder="Select countries"
            ></v-select>
          </v-col>

          <v-col cols="12" lg="3">
            <label class="control-label">Impact</label>
            <v-select
              v-model="selectedImpacts"
              :items="impactOptions"
              item-text="text"
              item-value="value"
              multiple
              chips
              small-chips
              deletable-chips
              hide-details
              outlined
              dense
              placeholder="Select impact"
            ></v-select>
          </v-col>
        </v-row>
      </v-card>

      <v-card elevation="0" class="search-panel">
        <div class="search-panel-header">
          <div>
            <p class="filter-eyebrow mb-1">Event lookup</p>
            <h3 class="mb-0">Search calendar results</h3>
          </div>
          <span>{{ filteredEvents.length }} matches</span>
        </div>

        <v-text-field
          v-model="search"
          placeholder="Search by event, category, country, currency, or symbol"
          prepend-inner-icon="mdi-magnify"
          hide-details
          outlined
          dense
          clearable
        ></v-text-field>
      </v-card>

      <v-alert v-if="errorMessage" type="error" dense text class="mt-4">
        {{ errorMessage }}
      </v-alert>

      <v-card elevation="0" class="calendar-table-card">
        <v-data-table
          :headers="headers"
          :items="filteredEvents"
          :loading="loading"
          :items-per-page="25"
          class="calendar-table"
          mobile-breakpoint="768"
        >
          <template v-slot:[`item.date`]="{ item }">
            <div class="date-cell">
              <strong>{{ formatTime(item.date) }}</strong>
              <span>{{ formatDay(item.date) }}</span>
            </div>
          </template>

          <template v-slot:[`item.country`]="{ item }">
            <div class="country-cell">
              <strong>{{ item.country?.abbr || "-" }}</strong>
              <span>{{ item.country?.currency || item.country?.name || "-" }}</span>
            </div>
          </template>

          <template v-slot:[`item.event`]="{ item }">
            <div class="event-cell">
              <strong>{{ item.event || "-" }}</strong>
              <span>{{ item.category || "Calendar" }}</span>
            </div>
          </template>

          <template v-slot:[`item.importance`]="{ item }">
            <v-chip
              small
              label
              :color="impactColor(item.importance)"
              :text-color="impactTextColor(item.importance)"
            >
              {{ impactLabel(item.importance) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actual`]="{ item }">
            <strong :class="valueClass(item.actual, item.forecast, item.better_than_forecast)">
              {{ displayValue(item.actual) }}
            </strong>
          </template>

          <template v-slot:[`item.forecast`]="{ item }">
            {{ displayValue(item.forecast || item.teforecast) }}
          </template>

          <template v-slot:[`item.previous`]="{ item }">
            {{ displayValue(item.previous) }}
          </template>

          <template v-slot:[`item.ticker_name`]="{ item }">
            <v-chip small outlined color="#02b58a">
              {{ item.ticker_name || item.ticker || "-" }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="46" color="#9fb3c8">mdi-calendar-alert</v-icon>
              <p>No economic events found for the selected filters.</p>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";

const API_URL = "https://apieconomic.finlogix.com/v1/getEconomicData";

export default {
  name: "EconomicCalendar",

  data() {
    return {
      loading: false,
      errorMessage: "",
      events: [],
      search: "",
      startDate: "",
      endDate: "",
      selectedCountries: ["US", "CH", "NZ", "CA", "CN", "FR", "AU", "JP", "EP", "GB", "DE", "IT"],
      selectedImpacts: [1, 2, 3],
      activeDatePreset: "next3",
      lastLoadedAt: null,
      headers: [
        { text: "Time", value: "date", width: 130 },
        { text: "Country", value: "country", width: 120 },
        { text: "Event", value: "event" },
        { text: "Impact", value: "importance", width: 110 },
        { text: "Actual", value: "actual", width: 110 },
        { text: "Forecast", value: "forecast", width: 110 },
        { text: "Previous", value: "previous", width: 110 },
        { text: "Symbol", value: "ticker_name", width: 120 },
      ],
      countryOptions: [
        { text: "United States", value: "US" },
        { text: "Switzerland", value: "CH" },
        { text: "New Zealand", value: "NZ" },
        { text: "Canada", value: "CA" },
        { text: "China", value: "CN" },
        { text: "France", value: "FR" },
        { text: "Australia", value: "AU" },
        { text: "Japan", value: "JP" },
        { text: "Euro Area", value: "EP" },
        { text: "United Kingdom", value: "GB" },
        { text: "Germany", value: "DE" },
        { text: "Italy", value: "IT" },
      ],
      impactOptions: [
        { text: "Low", value: 1 },
        { text: "Medium", value: 2 },
        { text: "High", value: 3 },
      ],
      datePresets: [
        { text: "Today", value: "today" },
        { text: "Tomorrow", value: "tomorrow" },
        { text: "Next 3 days", value: "next3" },
        { text: "This week", value: "week" },
      ],
    };
  },

  computed: {
    actionButtonColor() {
      return this.$vuetify.theme.dark ? "#02b58a" : "#132136";
    },

    filteredEvents() {
      const term = this.search.trim().toLowerCase();

      return this.events
        .filter((item) => this.selectedImpacts.includes(Number(item.importance || 1)))
        .filter((item) => {
          if (!term) return true;

          const searchable = [
            item.event,
            item.category,
            item.country?.name,
            item.country?.abbr,
            item.country?.currency,
            item.ticker_name,
            item.ticker,
            item.symbol,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchable.includes(term);
        })
        .sort((a, b) => new Date(this.normalizedDate(a.date)) - new Date(this.normalizedDate(b.date)));
    },

    highImpactCount() {
      return this.filteredEvents.filter((item) => Number(item.importance) >= 3).length;
    },

    lastLoadedText() {
      if (!this.lastLoadedAt) return "-";
      return this.lastLoadedAt.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },

  mounted() {
    this.setDefaultDates();
    this.fetchEconomicData();
  },

  methods: {
    setDefaultDates() {
      this.applyDatePreset("next3", false);
    },

    applyDatePreset(preset, shouldFetch = false) {
      const start = new Date();
      const end = new Date();

      if (preset === "tomorrow") {
        start.setDate(start.getDate() + 1);
        end.setDate(end.getDate() + 1);
      } else if (preset === "next3") {
        end.setDate(start.getDate() + 3);
      } else if (preset === "week") {
        end.setDate(start.getDate() + 7);
      }

      this.startDate = this.toDateInput(start);
      this.endDate = this.toDateInput(end);
      this.activeDatePreset = preset;

      if (shouldFetch) {
        this.fetchEconomicData();
      }
    },

    resetFilters() {
      this.search = "";
      this.selectedCountries = this.countryOptions.map((country) => country.value);
      this.selectedImpacts = this.impactOptions.map((impact) => impact.value);
      this.applyDatePreset("next3", true);
    },

    async fetchEconomicData() {
      if (!this.startDate || !this.endDate || !this.selectedCountries.length) {
        this.errorMessage = "Select a start date, end date, and at least one country.";
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      try {
        const { data } = await axios.get(API_URL, {
          params: {
            start: `${this.startDate}T00:00:00Z`,
            end: `${this.endDate}T23:59:59Z`,
            countries: this.selectedCountries.join(","),
          },
        });

        this.events = Array.isArray(data?.data) ? data.data : [];
        this.lastLoadedAt = new Date();
      } catch (error) {
        console.error(error);
        this.events = [];
        this.errorMessage = "Unable to load the economic calendar. If this continues, the API may need a backend proxy.";
      } finally {
        this.loading = false;
      }
    },

    toDateInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    normalizedDate(date) {
      if (!date) return "";
      return date.includes("T") ? date : date.replace(" ", "T");
    },

    formatTime(date) {
      if (!date) return "-";
      return new Date(this.normalizedDate(date)).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDay(date) {
      if (!date) return "-";
      return new Date(this.normalizedDate(date)).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },

    displayValue(value) {
      return value || "-";
    },

    impactLabel(importance) {
      const level = Number(importance);
      if (level >= 3) return "High";
      if (level === 2) return "Medium";
      return "Low";
    },

    impactColor(importance) {
      const level = Number(importance);
      if (level >= 3) return "#e55353";
      if (level === 2) return "#ffd166";
      return this.$vuetify.theme.dark ? "#26384f" : "#d8e3ec";
    },

    impactTextColor(importance) {
      const level = Number(importance);
      if (level >= 3) return "white";
      if (level === 2) return "#132136";
      return this.$vuetify.theme.dark ? "#e0eaf2" : "#132136";
    },

    valueClass(actual, forecast, direction) {
      if (!actual || !forecast || !direction) return "";

      const actualNumber = parseFloat(String(actual).replace(/[^0-9.-]/g, ""));
      const forecastNumber = parseFloat(String(forecast).replace(/[^0-9.-]/g, ""));

      if (Number.isNaN(actualNumber) || Number.isNaN(forecastNumber)) return "";

      const isBetter =
        direction === "larger"
          ? actualNumber > forecastNumber
          : actualNumber < forecastNumber;

      return isBetter ? "value-positive" : "value-negative";
    },
  },
};
</script>

<style scoped>
.economic-calendar-page {
  --calendar-page-bg: #e0eaf2;
  --calendar-surface: #ffffff;
  --calendar-surface-soft: #e8f1f8;
  --calendar-text: #132136;
  --calendar-muted: #607083;
  --calendar-border: rgba(19, 33, 54, 0.08);
  --calendar-table-hover: rgba(2, 181, 138, 0.06);
  --calendar-input-bg: #ffffff;
  background: var(--calendar-page-bg);
  color: var(--calendar-text);
}

.calendar-dark {
  --calendar-page-bg: #131c29;
  --calendar-surface: #162334;
  --calendar-surface-soft: #1a2a3e;
  --calendar-text: #e0eaf2;
  --calendar-muted: #9fb3c8;
  --calendar-border: rgba(224, 234, 242, 0.1);
  --calendar-table-hover: rgba(2, 181, 138, 0.12);
  --calendar-input-bg: #172233;
}

.calendar-shell {
  background: var(--calendar-page-bg);
}

.calendar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.section-label {
  color: #02b58a;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.calendar-header h2 {
  color: var(--calendar-text);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.calendar-subtitle {
  color: var(--calendar-muted);
  font-size: 14px;
}

.refresh-btn {
  color: #ffffff;
  min-width: 120px;
}

.summary-row {
  margin-bottom: 12px;
}

.summary-card {
  min-height: 82px;
  background: var(--calendar-surface);
  border: 1px solid var(--calendar-border);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.summary-card span {
  color: var(--calendar-muted);
  font-size: 12px;
  font-weight: 600;
}

.summary-card strong {
  color: var(--calendar-text);
  font-size: 24px;
  line-height: 1;
}

.summary-card--high {
  border-left: 4px solid #e55353;
}

.filter-panel {
  background: var(--calendar-surface);
  padding: 16px;
  border: 1px solid var(--calendar-border);
}

.search-panel {
  background: var(--calendar-surface);
  padding: 16px;
  margin-top: 12px;
  border: 1px solid var(--calendar-border);
}

.filter-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--calendar-border);
}

.search-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.search-panel-header h3 {
  color: var(--calendar-text);
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
}

.search-panel-header span {
  color: #02b58a;
  background: rgba(2, 181, 138, 0.12);
  border: 1px solid rgba(2, 181, 138, 0.24);
  font-size: 12px;
  font-weight: 700;
  padding: 5px 9px;
  white-space: nowrap;
}

.filter-eyebrow {
  color: #02b58a;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.filter-header h3 {
  color: var(--calendar-text);
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
}

.filter-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.reset-filter-btn {
  color: var(--calendar-muted) !important;
}

.quick-range-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.quick-range-row span {
  color: var(--calendar-muted);
  font-size: 12px;
  font-weight: 700;
  margin-right: 4px;
}

.quick-range-chip {
  background: var(--calendar-surface-soft) !important;
  color: var(--calendar-muted) !important;
  border: 1px solid var(--calendar-border);
  cursor: pointer;
  transition: 0.16s ease;
}

.quick-range-chip:hover,
.quick-range-chip--active {
  background: rgba(2, 181, 138, 0.14) !important;
  color: #02b58a !important;
  border-color: rgba(2, 181, 138, 0.36);
}

.filter-grid {
  row-gap: 10px;
}

.control-label {
  display: block;
  color: var(--calendar-muted);
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.calendar-table-card {
  background: var(--calendar-surface);
  margin-top: 14px;
  overflow: hidden;
  border: 1px solid var(--calendar-border);
}

.calendar-table {
  color: var(--calendar-text);
  background: var(--calendar-surface);
}

.date-cell,
.country-cell,
.event-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.date-cell strong,
.country-cell strong,
.event-cell strong {
  color: var(--calendar-text);
  font-size: 13px;
}

.date-cell span,
.country-cell span,
.event-cell span {
  color: var(--calendar-muted);
  font-size: 12px;
}

.event-cell strong {
  white-space: normal;
}

.value-positive {
  color: #02a77f;
}

.value-negative {
  color: #d64545;
}

.empty-state {
  padding: 36px 12px;
  text-align: center;
  color: var(--calendar-muted);
}

.calendar-table-card ::v-deep .v-data-table {
  background: var(--calendar-surface);
  color: var(--calendar-text);
}

.calendar-table-card ::v-deep .v-data-table-header th {
  background: var(--calendar-surface-soft);
  color: var(--calendar-muted) !important;
  border-bottom: 1px solid var(--calendar-border) !important;
}

.calendar-table-card ::v-deep .v-data-table tbody tr {
  background: var(--calendar-surface);
}

.calendar-table-card ::v-deep .v-data-table tbody tr:hover {
  background: var(--calendar-table-hover) !important;
}

.calendar-table-card ::v-deep .v-data-table td {
  color: var(--calendar-text);
  border-bottom: 1px solid var(--calendar-border) !important;
}

.calendar-table-card ::v-deep .v-data-footer {
  background: var(--calendar-surface);
  color: var(--calendar-muted);
  border-top: 1px solid var(--calendar-border);
}

.calendar-table-card ::v-deep .v-data-footer__select,
.calendar-table-card ::v-deep .v-data-footer__pagination {
  color: var(--calendar-muted);
}

.filter-panel ::v-deep .v-input__slot,
.search-panel ::v-deep .v-input__slot {
  background: var(--calendar-input-bg) !important;
  color: var(--calendar-text);
  min-height: 40px !important;
}

.filter-panel ::v-deep .v-label,
.filter-panel ::v-deep input,
.filter-panel ::v-deep .v-select__selection,
.filter-panel ::v-deep .v-icon,
.search-panel ::v-deep .v-label,
.search-panel ::v-deep input,
.search-panel ::v-deep .v-icon {
  color: var(--calendar-muted) !important;
}

.filter-panel ::v-deep fieldset,
.search-panel ::v-deep fieldset {
  border-color: var(--calendar-border) !important;
}

.filter-panel ::v-deep .v-chip {
  background: var(--calendar-surface-soft) !important;
  color: var(--calendar-text) !important;
  max-width: 130px;
}

.filter-panel ::v-deep .v-chip__content {
  min-width: 0;
}

.filter-panel ::v-deep .v-select .v-chip__content span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-panel ::v-deep .v-chip__close {
  width: 18px !important;
  height: 18px !important;
  min-width: 18px !important;
  border-radius: 50% !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  flex: 0 0 18px;
  background: rgba(96, 112, 131, 0.18);
  color: var(--calendar-muted) !important;
  opacity: 1;
}

.filter-panel ::v-deep .v-chip__close::before {
  border-radius: 50% !important;
}

.filter-panel ::v-deep .v-chip__close:hover {
  background: rgba(229, 83, 83, 0.16);
  color: #e55353 !important;
}

@media (max-width: 768px) {
  .calendar-shell {
    padding: 12px;
  }

  .calendar-header {
    flex-direction: column;
  }

  .refresh-btn {
    width: 100%;
  }

  .filter-header {
    flex-direction: column;
  }

  .filter-actions,
  .filter-actions .v-btn {
    width: 100%;
  }

  .quick-range-row {
    align-items: flex-start;
  }

  .quick-range-row span {
    width: 100%;
  }

  .search-panel-header {
    flex-direction: column;
  }
}

.v-sheet .v-card{
  border-radius: 0px !important;
}
</style>
